import React, { useState, useEffect, useRef } from "react";
import { Button, notification } from "antd";
import { CheckOutlined, IssuesCloseOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import devtools from "devtools-detect";
import { useNavigate, useParams } from "react-router-dom";
import { putData } from "../../../actions/developer";
import { decryptData, encryptData } from "../../../actions/crypto";
import { getData } from "../../../actions/apiCall";
import { examConfig } from "../../../config/examConfig";


interface CheckSystemProps {
    onReload?: () => void;
    camera: boolean;
    face: { faceDetected: boolean; singleFace: boolean };
}


const TestSystemConfig: React.FC<CheckSystemProps> = ({
    onReload,
    camera,
    face,
}) => {
    const { id } = useParams();
    const [checks, setChecks] = useState({
        camera: !examConfig.enableSystemChecks, // Set based on config
        audio: !examConfig.enableSystemChecks, // Set based on config
        singleFace: !examConfig.enableSystemChecks, // Set based on config
        faceDetected: !examConfig.enableSystemChecks, // Set based on config
        fullscreen: !examConfig.enableFullscreenCheck, // Set based on config
        devtools: !examConfig.enableSystemChecks, // Set based on config
    });

    const [counter, setCounter] = useState(0);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        if (examConfig.enableSystemChecks) {
            setChecks((prev) => ({
                ...prev,
                camera: camera,
                audio: camera,
                faceDetected: face.faceDetected,
                singleFace: face.singleFace
            }));
        }
    }, [face, camera, examConfig.enableSystemChecks]);

    useEffect(() => {
        if (examConfig.enableFullscreenCheck) {
            // Initial check
            setChecks((prev) => ({ ...prev, fullscreen: !!document.fullscreenElement }));

            // Set up event listeners
            const handler = () => {
                setChecks((prev) => ({ ...prev, fullscreen: !!document.fullscreenElement }));
            };

            document.addEventListener("fullscreenchange", handler);
            document.addEventListener("webkitfullscreenchange", handler);
            document.addEventListener("mozfullscreenchange", handler);
            document.addEventListener("MSFullscreenChange", handler);

            return () => {
                document.removeEventListener("fullscreenchange", handler);
                document.removeEventListener("webkitfullscreenchange", handler);
                document.removeEventListener("mozfullscreenchange", handler);
                document.removeEventListener("MSFullscreenChange", handler);
            };
        }
    }, []);

    useEffect(() => {
        if (examConfig.enableSystemChecks) {
            window.addEventListener("resize", checkDevTools);
            return () => {
                window.removeEventListener("resize", checkDevTools);
            };
        }
    }, []);

    const checkDevTools = () => {
        setChecks((prev) => ({ ...prev, devtools: !devtools.isOpen }));
    };

    const startExam = async () => {
        try {
            const resData = await dispatch(getData(`/accel-test/${id}`));
            if (resData.code === 200) {
                const data = decryptData(resData.data);

                if (!data) {
                    notification.error({ message: 'Test Not Found' });
                    navigate(`/instructions/${id}`); // Redirect to Error page
                    return;
                }

                if (data?.testStatus === 'completed') {
                    notification.error({ message: 'Test Already Completed' });
                    navigate(`/instructions/${id}`); // Redirect to Error page
                    return;
                }

                if (data?.testStatus === 'expired') {
                    notification.error({ message: 'Test Expired' });
                    navigate(`/instructions/${id}`); // Redirect to Error page
                    return;
                }

                if (data?.testStatus === 'cancelled') {
                    notification.error({ message: 'Test Cancelled' });
                    navigate(`/instructions/${id}`); // Redirect to Error page
                    return;
                }

                let type = 'mcq';
                if (data?.testType === 'ai-interview' || data?.aiInterview) {
                    type = 'ai-interview';
                } else if (data?.mcqQuestions?.length > 0) {
                    type = 'mcq';
                } else if (data?.mcqQuestions?.length == 0 && data?.coddingQuestions?.length > 0) {
                    type = 'coding';
                }

                let conditon = {};
                if (type === 'mcq') {
                    conditon = { mcqStartedAt: new Date() };
                } else if (type === 'coding') {
                    conditon = { coddingStartedAt: new Date() };
                } else if (type === 'ai-interview') {
                    conditon = { aiInterviewStartedAt: new Date() };
                }

                const testDetails = await dispatch(putData(`/accel-test/test/started/${id}`, {
                    data: encryptData({
                        ...conditon,
                        testStage: type,
                        testStatus: 'inporgress',
                    })
                }))
                if (testDetails) {
                    navigate(`/online-exam/${id}/exam`);
                }
            }
        } catch (error) {
            navigate(`/instructions/${id}`); // Redirect to Error page
        }
    }

    useEffect(() => {
        if (examConfig.enableSystemChecks || examConfig.enableFullscreenCheck) {
            // Original behavior: wait for all checks to pass
            if (Object.values(checks).every((val) => val)) {
                if (counter == 0) {
                    startExam();
                }
                setTimeout(() => {
                    setCounter(counter - 1);
                }, 1000);
            } else {
                setCounter(0);
            }
        } else {
            // Disabled checks: start immediately
            if (counter == 0) {
                startExam();
            }
            if (counter > 0) {
                setTimeout(() => {
                    setCounter(counter - 1);
                }, 1000);
            }
        }
    }, [counter]);

    useEffect(() => {
        if (examConfig.enableSystemChecks || examConfig.enableFullscreenCheck) {
            // Original behavior: wait for all checks
            if (Object.values(checks).every((val) => val)) {
                if (counter == 0) {
                    setCounter(5);
                }
            }
        } else {
            // Disabled checks: start immediately
            if (counter == 0) {
                setCounter(0);
            }
        }
    }, [checks]);



    const requestFullscreen = async () => {
        try {
            const element = document.documentElement;
            if (element.requestFullscreen) {
                await element.requestFullscreen();
            } else if ((element as any).webkitRequestFullscreen) {
                await (element as any).webkitRequestFullscreen();
            } else if ((element as any).mozRequestFullScreen) {
                await (element as any).mozRequestFullScreen();
            } else if ((element as any).msRequestFullscreen) {
                await (element as any).msRequestFullscreen();
            }
        } catch (error) {
            console.error('Error requesting fullscreen:', error);
            notification.error({
                message: 'Failed to enter fullscreen mode',
                description: 'Please press F11 or use your browser\'s fullscreen option.'
            });
        }
    };

    const retryChecks = async () => {
        // Re-check fullscreen status
        if (examConfig.enableFullscreenCheck) {
            const isFullscreen = !!(document.fullscreenElement ||
                (document as any).webkitFullscreenElement ||
                (document as any).mozFullScreenElement ||
                (document as any).msFullscreenElement);
            setChecks((prev) => ({ ...prev, fullscreen: isFullscreen }));
        }
        if (onReload) {
            onReload();
        }
    };

    const configurations = [
        { label: "Camera", description: "Must be enabled and working properly.", status: checks.camera },
        { label: "Audio", description: "Microphone should be functional.", status: checks.audio },
        { label: "Face Detected", description: "Ensure your face is clearly visible.", status: checks.faceDetected },
        { label: "Single Face Only", description: "No additional faces should be in the frame.", status: checks.singleFace },
        { label: "Fullscreen Mode", description: "Your browser must be in full-screen mode.", status: checks.fullscreen },
        { label: 'Inspect Element Disabled', description: 'Inspect element should not use during test', status: checks.devtools },
    ];

    return (
        <>
            <div className="flex flex-col items-center p-6 bg-[#d0efef] min-h-screen pt-40">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    {configurations.map(({ label, description, status }) => (
                        <div
                            key={label}
                            className={`flex items-center p-4 rounded-lg w-100 bg-white`}>
                            <div className={`${status ? "bg-[#009921]" : "bg-[#B60505]"} flex items-center justify-center w-10 h-10 rounded-full mr-2`}>
                                {status ? (
                                    <CheckOutlined className="text-white text-[20px]" />
                                ) : (
                                    <IssuesCloseOutlined className="text-white text-[20px]" />
                                )}
                            </div>
                            <div className="max-w-80">
                                <p className="font-semibold">{label}</p>
                                <p className="text-sm text-gray-700">{description}</p>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="flex flex-col items-center gap-2">
                    {examConfig.enableFullscreenCheck && !checks.fullscreen && (
                        <Button
                            type="default"
                            onClick={requestFullscreen}
                            style={{ marginTop: "20px" }}
                            className="py-2 px-4"
                        >
                            Enter Fullscreen Mode
                        </Button>
                    )}
                    <Button
                        type="primary"
                        onClick={examConfig.enableSystemChecks || examConfig.enableFullscreenCheck ? retryChecks : startExam}
                        disabled={examConfig.enableSystemChecks || examConfig.enableFullscreenCheck ? Object.values(checks).every((val) => val) : false}
                        style={{ marginTop: "20px" }}
                        className="py-2 px-4"
                    >
                        {examConfig.enableSystemChecks || examConfig.enableFullscreenCheck ? "Retry Checks" : "Start Exam"}
                    </Button>
                </div>
                {(examConfig.enableSystemChecks || examConfig.enableFullscreenCheck ? Object.values(checks).every((val) => val) : true) && counter > 0 && (
                    <>
                        <p className="text-lg text-green-500 mt-4">All configurations are enabled. Your Test will start in {counter} Seconds</p>
                    </>
                )}

                <p className="text-xs text-gray-500 mt-4 text-center max-w-lg">
                    To continue with the test, all configurations must be enabled. Once they are enabled, you
                    will be able to proceed. If any configuration is turned off during the test, it may result
                    in automatic submission.
                </p>
            </div>
        </>
    );
};

export default TestSystemConfig;